import { useState, type FormEvent } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Alert, Badge, Button, Card, Field } from '@/components/ui';

export function AccountPage() {
  const { user, mustChangePassword, refresh } = useAuth();
  const [currentPassword, setCurrent] = useState('');
  const [newPassword, setNew] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setDone(false);

    if (newPassword !== confirmation) {
      setError('Les deux mots de passe ne correspondent pas.');
      return;
    }

    setBusy(true);
    try {
      await api.post('/api/auth/password', { currentPassword, newPassword });
      setDone(true);
      setCurrent('');
      setNew('');
      setConfirmation('');
      await refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Modification impossible.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-lg space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-100">Mon compte</h1>
        <p className="mt-1 flex items-center gap-2 text-sm text-slate-400">
          {user?.displayName}
          <Badge tone={user?.role === 'admin' ? 'info' : 'neutre'}>
            {user?.role === 'admin' ? 'administrateur' : 'joueur'}
          </Badge>
        </p>
      </div>

      {mustChangePassword && (
        <Alert type="info">
          Votre mot de passe a été généré par l’administrateur : changez-le dès maintenant.
        </Alert>
      )}

      <Card title="Changer de mot de passe">
        <form onSubmit={(e) => void submit(e)} className="space-y-4">
          <Field
            label="Mot de passe actuel"
            type="password"
            autoComplete="current-password"
            required
            value={currentPassword}
            onChange={(e) => setCurrent(e.target.value)}
          />
          <Field
            label="Nouveau mot de passe"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            hint="8 caractères au minimum."
            value={newPassword}
            onChange={(e) => setNew(e.target.value)}
          />
          <Field
            label="Confirmation"
            type="password"
            autoComplete="new-password"
            required
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
          />
          {error && <Alert>{error}</Alert>}
          {done && <Alert type="succes">Mot de passe mis à jour.</Alert>}
          <Button type="submit" disabled={busy}>
            {busy ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </form>
      </Card>
    </div>
  );
}
